import { useMemo } from 'react';
import type { UIStyle, Position } from '../../../types';

export interface UseUIStyleOptions {
  uiStyle?: UIStyle;
  position?: Position;
}

export interface UIStyleConfig {
  /** Style identifier */
  style: UIStyle;
  /** Button size in pixels */
  buttonSize: number;
  /** Panel width in pixels */
  panelWidth: number;
  /** Panel max height in pixels */
  panelMaxHeight: number;
  /** Border radius */
  borderRadius: number;
  /** Avatar size in pixels */
  avatarSize: number;
  /** Header padding */
  headerPadding: number;
  /** Content padding */
  contentPadding: number;
  /** Base font size */
  fontSizeBase: number;
  /** CSS class to apply */
  className: string;
  /** Whether to show minimize button */
  showMinimize: boolean;
  /** Whether to show transcript */
  showTranscript: boolean;
  /** Whether panel uses fixed positioning */
  isFixed: boolean;
}

const STYLE_CONFIGS: Record<UIStyle, UIStyleConfig> = {
  default: {
    style: 'default',
    buttonSize: 64,
    panelWidth: 380,
    panelMaxHeight: 600,
    borderRadius: 24,
    avatarSize: 48,
    headerPadding: 16,
    contentPadding: 16,
    fontSizeBase: 14,
    className: 'qcall-style-default',
    showMinimize: true,
    showTranscript: true,
    isFixed: true,
  },
  minimal: {
    style: 'minimal',
    buttonSize: 44,
    panelWidth: 320,
    panelMaxHeight: 480,
    borderRadius: 12,
    avatarSize: 32,
    headerPadding: 12,
    contentPadding: 12,
    fontSizeBase: 12,
    className: 'qcall-style-minimal',
    showMinimize: false,
    showTranscript: true,
    isFixed: true,
  },
  expanded: {
    style: 'expanded',
    buttonSize: 56,
    panelWidth: 450,
    panelMaxHeight: 700,
    borderRadius: 0,
    avatarSize: 40,
    headerPadding: 16,
    contentPadding: 16,
    fontSizeBase: 14,
    className: 'qcall-style-expanded',
    showMinimize: false,
    showTranscript: true,
    isFixed: false,
  },
};

/**
 * Hook to get UI style configuration based on the provided style
 */
export function useUIStyle(options: UseUIStyleOptions = {}): UIStyleConfig {
  const { uiStyle = 'default' } = options;

  return useMemo(() => {
    return STYLE_CONFIGS[uiStyle] || STYLE_CONFIGS.default;
  }, [uiStyle]);
}

/**
 * Get the default position for a given UI style
 */
export function getDefaultPositionForStyle(uiStyle: UIStyle): Position {
  switch (uiStyle) {
    case 'minimal':
      return 'bottom-left';
    case 'default':
    case 'expanded':
    default:
      return 'bottom-right';
  }
}

/**
 * Determine the best UI style based on position
 */
export function getStyleForPosition(position: Position): UIStyle {
  switch (position) {
    case 'bottom-left':
      return 'minimal';
    case 'bottom-right':
    default:
      return 'default';
  }
}
